import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { format, differenceInYears } from 'date-fns'
import { fr } from 'date-fns/locale'
import SchemaCorporel from './SchemaCorporel'

function initiales(prenom, nom) {
  return ((prenom?.[0] || '') + (nom?.[0] || '')).toUpperCase()
}
function avatarClass(nom) {
  return nom?.charCodeAt(0) % 2 === 0 ? 'v2' : ''
}
function getAge(dob) {
  if (!dob) return null
  return differenceInYears(new Date(), new Date(dob))
}
function profilLabel(patient) {
  if (patient.enceinte) return { txt: '🤰 Enceinte', color: '#D4537E' }
  const age = getAge(patient.date_naissance)
  if (age === null) return null
  if (age < 12) return { txt: `🧒 Enfant (${age} ans)`, color: '#854F0B' }
  if (age < 18) return { txt: `🧑 Ado (${age} ans)`, color: '#185FA5' }
  if (age >= 75) return { txt: `👴 Senior (${age} ans)`, color: '#5F5E5A' }
  return null
}

function emptyAntecedent() {
  return { categorie: 'medical', description: '', date_evenement: '' }
}
function emptyActivite() {
  return { activite: '', niveau: 'loisir', frequence: '' }
}

export default function Patients() {
  const { praticien } = useAuth()
  const [patients, setPatients] = useState([])
  const [filtered, setFiltered] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [panel, setPanel] = useState(null)
  const [activeTab, setActiveTab] = useState('info')
  const [saving, setSaving] = useState(false)

  // Fiche principale
  const [form, setForm] = useState(emptyForm())
  // Données liées
  const [consultations, setConsultations] = useState([])
  const [factures, setFactures] = useState([])
  const [antecedents, setAntecedents] = useState([])
  const [activites, setActivites] = useState([])
  const [liens, setLiens] = useState([]) // parents/enfants liés

  // Formulaires inline
  const [newAntecedent, setNewAntecedent] = useState(emptyAntecedent())
  const [newActivite, setNewActivite] = useState(emptyActivite())
  const [searchLien, setSearchLien] = useState('')
  const [searchLienResults, setSearchLienResults] = useState([])
  const [newLienRole, setNewLienRole] = useState('mere')

  function emptyForm() {
    return {
      prenom: '', nom: '', genre: '', date_naissance: '', telephone: '', email: '',
      adresse: '', code_postal: '', ville: '', profession: '', medecin_traitant: '', numero_secu: '',
      enceinte: false, date_terme: '', notes: '', antecedents: ''
    }
  }

  const load = useCallback(async () => {
    setLoading(true)
    // 1. Charger tous les patients
    let all = []
    let from = 0
    const batch = 1000
    while (true) {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .order('nom')
        .range(from, from + batch - 1)
      if (error || !data || data.length === 0) break
      all = [...all, ...data]
      if (data.length < batch) break
      from += batch
    }
    // 2. Compter les consultations par patient (requête séparée, robuste)
    const countMap = {}
    let cfrom = 0
    while (true) {
      const { data: crows, error: cerr } = await supabase
        .from('consultations')
        .select('patient_id')
        .range(cfrom, cfrom + 999)
      if (cerr || !crows || crows.length === 0) break
      for (const row of crows) {
        if (row.patient_id) countMap[row.patient_id] = (countMap[row.patient_id] || 0) + 1
      }
      if (crows.length < 1000) break
      cfrom += 1000
    }
    // 3. Attacher le compte à chaque patient (forme attendue par la colonne : consultations[0].count)
    const withCounts = all.map(p => ({ ...p, consultations: [{ count: countMap[p.id] || 0 }] }))
    setPatients(withCounts)
    setFiltered(withCounts)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // Ouvrir automatiquement un patient si navigation depuis Consultations
  useEffect(() => {
    const patientId = localStorage.getItem('open_patient_id')
    if (patientId) {
      localStorage.removeItem('open_patient_id')
      supabase.from('patients').select('*').eq('id', patientId).single()
        .then(({ data }) => { if (data) openPatient(data) })
    }
  }, [])

  useEffect(() => {
    const handler = () => { setForm(emptyForm()); setPanel('new'); setActiveTab('info') }
    window.addEventListener('cabinet:new', handler)
    return () => window.removeEventListener('cabinet:new', handler)
  }, [])

  useEffect(() => {
    const mots = search.toLowerCase().trim().split(/\s+/).filter(Boolean)
    setFiltered(mots.length ? patients.filter(p => {
      const texte = `${p.prenom || ''} ${p.nom || ''} ${p.telephone || ''} ${p.email || ''}`.toLowerCase()
      return mots.every(mot => texte.includes(mot))
    }) : patients)
  }, [search, patients])

  async function openPatient(patient) {
    setPanel(patient)
    setForm({
      prenom: patient.prenom || '', nom: patient.nom || '',
      genre: patient.genre || '', date_naissance: patient.date_naissance || '',
      telephone: patient.telephone || '', email: patient.email || '',
      adresse: patient.adresse || '', code_postal: patient.code_postal || '', ville: patient.ville || '', profession: patient.profession || '',
      medecin_traitant: patient.medecin_traitant || '',
      enceinte: patient.enceinte || false, date_terme: patient.date_terme || '',
      notes: patient.notes || '', antecedents: patient.antecedents || ''
    })
    setActiveTab('info')
    loadPatientData(patient.id)
  }

  async function loadPatientData(patientId) {
    const [c, f, ant, act, lns] = await Promise.all([
      supabase.from('consultations').select('*, praticiens(nom, prenom)').eq('patient_id', patientId).order('date_consultation', { ascending: false }),
      supabase.from('factures').select('*').eq('patient_id', patientId).order('date_facture', { ascending: false }),
      supabase.from('patient_antecedents').select('*').eq('patient_id', patientId).order('date_evenement', { ascending: false }),
      supabase.from('patient_activites').select('*').eq('patient_id', patientId),
      supabase.from('patient_liens').select('*, lie:lie_id(id, prenom, nom, date_naissance)').eq('patient_id', patientId),
    ])
    setConsultations(c.data || [])
    setFactures(f.data || [])
    setAntecedents(ant.data || [])
    setActivites(act.data || [])
    setLiens(lns.data || [])
  }

  async function savePatient() {
    setSaving(true)
    const payload = {
      prenom: form.prenom, nom: form.nom, genre: form.genre || null,
      date_naissance: form.date_naissance || null,
      telephone: form.telephone, email: form.email, adresse: form.adresse,
      code_postal: form.code_postal, ville: form.ville,
      profession: form.profession, medecin_traitant: form.medecin_traitant,
      enceinte: form.enceinte, date_terme: form.date_terme || null,
      notes: form.notes, antecedents: form.antecedents
    }
    if (panel === 'new') {
      payload.created_by = praticien.id
      const { error } = await supabase.from('patients').insert(payload)
      if (error) { alert('Erreur : ' + error.message); setSaving(false); return }
      setPanel(null); load()
    } else {
      const { error } = await supabase.from('patients').update(payload).eq('id', panel.id)
      if (error) { alert('Erreur : ' + error.message); setSaving(false); return }
      load(); setPanel({ ...panel, ...payload })
    }
    setSaving(false)
  }

  async function deletePatient() {
    if (!window.confirm(`Supprimer ${panel.prenom} ${panel.nom} ? Cette action est irréversible.`)) return
    await supabase.from('patients').delete().eq('id', panel.id)
    setPanel(null); load()
  }

  // Antécédents
  async function addAntecedent() {
    if (!newAntecedent.description) return
    await supabase.from('patient_antecedents').insert({
      patient_id: panel.id, ...newAntecedent,
      date_evenement: newAntecedent.date_evenement || null
    })
    setNewAntecedent(emptyAntecedent())
    loadPatientData(panel.id)
  }
  async function deleteAntecedent(id) {
    await supabase.from('patient_antecedents').delete().eq('id', id)
    loadPatientData(panel.id)
  }

  // Activités
  async function addActivite() {
    if (!newActivite.activite) return
    await supabase.from('patient_activites').insert({ patient_id: panel.id, ...newActivite })
    setNewActivite(emptyActivite())
    loadPatientData(panel.id)
  }
  async function deleteActivite(id) {
    await supabase.from('patient_activites').delete().eq('id', id)
    loadPatientData(panel.id)
  }

  // Liens familiaux
  async function searchPatientLien(q) {
    setSearchLien(q)
    if (q.length < 2) { setSearchLienResults([]); return }
    const { data } = await supabase.from('patients').select('id, prenom, nom, date_naissance')
      .or(`nom.ilike.%${q}%,prenom.ilike.%${q}%`).neq('id', panel.id).limit(6)
    setSearchLienResults(data || [])
  }
  async function addLien(lie) {
    await supabase.from('patient_liens').insert({
      patient_id: panel.id, lie_id: lie.id, role: newLienRole, facturation_principale: false
    })
    setSearchLien(''); setSearchLienResults([])
    loadPatientData(panel.id)
  }
  async function toggleFacturationPrincipale(lienId, val) {
    await supabase.from('patient_liens').update({ facturation_principale: val }).eq('id', lienId)
    loadPatientData(panel.id)
  }
  async function deleteLien(id) {
    await supabase.from('patient_liens').delete().eq('id', id)
    loadPatientData(panel.id)
  }

  const ROLES = { mere: 'Mère', pere: 'Père', tuteur: 'Tuteur/tutrice', enfant: 'Enfant' }
  const CATEGORIES = { medical: 'Médical', traumatique: 'Traumatique', chirurgie: 'Chirurgie' }
  const CAT_COLORS = { medical: '#185FA5', traumatique: '#854F0B', chirurgie: '#A32D2D' }
  const CAT_BG = { medical: '#E6F1FB', traumatique: '#FAEEDA', chirurgie: '#FCEBEB' }
  const NIVEAUX = { loisir: 'Loisir', competition: 'Compétition', professionnel: 'Professionnel' }

  const antMedical = antecedents.filter(a => a.categorie === 'medical')
  const antTrauma = antecedents.filter(a => a.categorie === 'traumatique')
  const antChir = antecedents.filter(a => a.categorie === 'chirurgie')

  return (
    <>
      <div className="stats-grid">
        <div className="stat-card"><div className="label">Total patients</div><div className="value">{patients.length}</div></div>
        <div className="stat-card"><div className="label">Avec dossier complet</div><div className="value">{patients.filter(p => p.date_naissance && p.telephone).length}</div></div>
        <div className="stat-card"><div className="label">Enfants (-18 ans)</div><div className="value">{patients.filter(p => getAge(p.date_naissance) !== null && getAge(p.date_naissance) < 18).length}</div></div>
        <div className="stat-card"><div className="label">Enceintes</div><div className="value">{patients.filter(p => p.enceinte).length}</div></div>
      </div>

      <div className="section-header"><span className="section-title">Tous les patients</span></div>

      <div className="search-bar">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="6.5" cy="6.5" r="4.5"/><path d="M10.5 10.5l3 3"/></svg>
        <input placeholder="Rechercher par nom, téléphone, email…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <div className="empty">Chargement…</div> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Patient</th><th>Âge</th><th>Téléphone</th><th>Profil</th><th>Consultations</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={5} className="empty">Aucun patient trouvé</td></tr>}
              {filtered.map(p => {
                const profil = profilLabel(p)
                return (
                  <tr key={p.id} className="clickable" onClick={() => openPatient(p)}>
                    <td>
                      <div className="cell-name">
                        <div className={`avatar ${avatarClass(p.nom)}`}>{initiales(p.prenom, p.nom)}</div>
                        <div>
                          <div>{p.prenom} {p.nom}</div>
                          {p.email && <div style={{fontSize:11, color:'var(--text-3)'}}>{p.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="td-muted">{getAge(p.date_naissance) !== null ? `${getAge(p.date_naissance)} ans` : '—'}</td>
                    <td className="td-muted">{p.telephone || '—'}</td>
                    <td>{profil ? <span style={{fontSize:12, color:profil.color, fontWeight:500}}>{profil.txt}</span> : <span className="td-muted">—</span>}</td>
                    <td className="td-muted">{p.consultations?.[0]?.count || 0}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* PANEL PATIENT */}
      {panel && (
        <>
          <div className="panel-overlay" onClick={() => setPanel(null)} />
          <div className="panel" style={{width: 640}}>
            <div className="panel-header">
              <div>
                <h3>{panel === 'new' ? 'Nouveau patient' : `${panel.prenom} ${panel.nom}`}</h3>
                {panel !== 'new' && getAge(panel.date_naissance) !== null && (
                  <div style={{fontSize:12, color:'var(--text-2)', marginTop:2}}>
                    {getAge(panel.date_naissance)} ans
                    {panel.enceinte ? ' · 🤰 Enceinte' : ''}
                  </div>
                )}
              </div>
              <button className="close-btn" onClick={() => setPanel(null)}>✕</button>
            </div>

            {panel !== 'new' && (
              <div className="tab-bar" style={{paddingLeft:20, paddingRight:20}}>
                {[
                  ['info', 'Identité'],
                  ['medical', 'Dossier médical'],
                  ['activites', 'Activités'],
                  ['famille', 'Famille'],
                  ['schema', 'Schéma corporel'],
                  ['consultations', 'Consultations'],
                  ['factures', 'Factures'],
                ].map(([key, label]) => (
                  <div key={key} className={`tab ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}
                    style={{fontSize:12, padding:'10px 10px'}}>
                    {label}
                  </div>
                ))}
              </div>
            )}

            <div className="panel-body">

              {/* ── IDENTITÉ ── */}
              {(panel === 'new' || activeTab === 'info') && (
                <div>
                  <div className="form-row">
                    <div className="form-group"><label>Prénom *</label><input value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} /></div>
                    <div className="form-group"><label>Nom *</label><input value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Genre</label>
                      <select value={form.genre} onChange={e => setForm({...form, genre: e.target.value})}>
                        <option value="">—</option>
                        <option value="F">Femme</option>
                        <option value="M">Homme</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                    <div className="form-group"><label>Date de naissance</label><input type="date" value={form.date_naissance} onChange={e => setForm({...form, date_naissance: e.target.value})} /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Téléphone</label><input value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} placeholder="06 xx xx xx xx" /></div>
                    <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
                  </div>
                  <div className="form-group" style={{marginBottom:14}}>
                    <label>Adresse</label><input value={form.adresse} onChange={e => setForm({...form, adresse: e.target.value})} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Code postal</label><input value={form.code_postal} onChange={e => setForm({...form, code_postal: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label>Ville</label><input value={form.ville} onChange={e => setForm({...form, ville: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Profession</label><input value={form.profession} onChange={e => setForm({...form, profession: e.target.value})} /></div>
                    <div className="form-group"><label>Médecin traitant</label><input value={form.medecin_traitant} onChange={e => setForm({...form, medecin_traitant: e.target.value})} placeholder="Dr. Nom" /></div>
                  </div>
                  <div className="form-group" style={{marginBottom:14}}>
                    <label>Numéro de sécurité sociale</label>
                    <input value={form.numero_secu} onChange={e => setForm({...form, numero_secu: e.target.value})} placeholder="1 85 06 75 123 456 78" maxLength={21} />
                  </div>

                  {/* Section grossesse — visible si genre F ou non renseigné */}
                  {(form.genre === 'F' || form.genre === '') && (
                    <div style={{background:'var(--bg)', borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:14}}>
                      <div style={{display:'flex', alignItems:'center', gap:10, marginBottom: form.enceinte ? 10 : 0}}>
                        <input type="checkbox" id="enceinte" checked={form.enceinte} onChange={e => setForm({...form, enceinte: e.target.checked})} style={{width:15,height:15,cursor:'pointer'}} />
                        <label htmlFor="enceinte" style={{fontSize:13, cursor:'pointer', margin:0, fontWeight:500}}>Patiente enceinte 🤰</label>
                      </div>
                      {form.enceinte && (
                        <div className="form-group" style={{marginTop:8, marginBottom:0}}>
                          <label>Date de terme prévue</label>
                          <input type="date" value={form.date_terme} onChange={e => setForm({...form, date_terme: e.target.value})} />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="form-group" style={{marginBottom:0}}>
                    <label>Notes libres</label>
                    <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Observations générales…" style={{minHeight:60}} />
                  </div>
                </div>
              )}

              {/* ── DOSSIER MÉDICAL ── */}
              {activeTab === 'medical' && (
                <div>
                  {/* Formulaire ajout */}
                  <div style={{background:'var(--bg)', borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:20}}>
                    <div style={{fontSize:12, fontWeight:500, color:'var(--text-2)', marginBottom:10}}>Ajouter un antécédent</div>
                    <div className="form-row" style={{marginBottom:8}}>
                      <div className="form-group">
                        <label>Catégorie</label>
                        <select value={newAntecedent.categorie} onChange={e => setNewAntecedent(a => ({...a, categorie: e.target.value}))}>
                          <option value="medical">Médical</option>
                          <option value="traumatique">Traumatique</option>
                          <option value="chirurgie">Chirurgie</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Date (approx.)</label>
                        <input type="date" value={newAntecedent.date_evenement} onChange={e => setNewAntecedent(a => ({...a, date_evenement: e.target.value}))} />
                      </div>
                    </div>
                    <div className="form-group" style={{marginBottom:10}}>
                      <label>Description *</label>
                      <input value={newAntecedent.description} onChange={e => setNewAntecedent(a => ({...a, description: e.target.value}))} placeholder="Ex: Hernie discale L4-L5, Fracture tibia gauche, Appendicectomie…" />
                    </div>
                    <button className="btn btn-primary btn-sm" style={{width:'100%', justifyContent:'center'}} onClick={addAntecedent} disabled={!newAntecedent.description}>
                      + Ajouter
                    </button>
                  </div>

                  {/* Affichage par catégorie */}
                  {[['medical', antMedical], ['traumatique', antTrauma], ['chirurgie', antChir]].map(([cat, items]) => (
                    <div key={cat} style={{marginBottom:20}}>
                      <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:8}}>
                        <span style={{fontSize:11, fontWeight:500, textTransform:'uppercase', letterSpacing:'.05em', color: CAT_COLORS[cat]}}>{CATEGORIES[cat]}</span>
                        <span style={{fontSize:11, color:'var(--text-3)'}}>({items.length})</span>
                      </div>
                      {items.length === 0 && <div style={{fontSize:12, color:'var(--text-3)', fontStyle:'italic'}}>Aucun antécédent {CATEGORIES[cat].toLowerCase()}</div>}
                      {items.map(a => (
                        <div key={a.id} style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8, padding:'8px 10px', background: CAT_BG[a.categorie], borderLeft:`3px solid ${CAT_COLORS[a.categorie]}`, borderRadius:'0 var(--radius) var(--radius) 0', marginBottom:6}}>
                          <div>
                            <div style={{fontSize:13, color:'var(--text)'}}>{a.description}</div>
                            {a.date_evenement && <div style={{fontSize:11, color:'var(--text-2)', marginTop:2}}>{format(new Date(a.date_evenement), 'MM/yyyy')}</div>}
                          </div>
                          <button className="btn btn-sm btn-danger" style={{flexShrink:0, padding:'2px 7px'}} onClick={() => deleteAntecedent(a.id)}>✕</button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* ── ACTIVITÉS ── */}
              {activeTab === 'activites' && (
                <div>
                  <div style={{background:'var(--bg)', borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:20}}>
                    <div style={{fontSize:12, fontWeight:500, color:'var(--text-2)', marginBottom:10}}>Ajouter une activité</div>
                    <div className="form-group" style={{marginBottom:8}}>
                      <label>Activité *</label>
                      <input value={newActivite.activite} onChange={e => setNewActivite(a => ({...a, activite: e.target.value}))} placeholder="Ex: Tennis, Yoga, Course à pied, Natation…" />
                    </div>
                    <div className="form-row" style={{marginBottom:10}}>
                      <div className="form-group">
                        <label>Niveau</label>
                        <select value={newActivite.niveau} onChange={e => setNewActivite(a => ({...a, niveau: e.target.value}))}>
                          <option value="loisir">Loisir</option>
                          <option value="competition">Compétition</option>
                          <option value="professionnel">Professionnel</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Fréquence</label>
                        <input value={newActivite.frequence} onChange={e => setNewActivite(a => ({...a, frequence: e.target.value}))} placeholder="Ex: 3x/semaine, weekends…" />
                      </div>
                    </div>
                    <button className="btn btn-primary btn-sm" style={{width:'100%', justifyContent:'center'}} onClick={addActivite} disabled={!newActivite.activite}>
                      + Ajouter
                    </button>
                  </div>

                  {activites.length === 0 && <div className="empty">Aucune activité enregistrée</div>}
                  {activites.map(a => (
                    <div key={a.id} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', border:'0.5px solid var(--border)', borderRadius:'var(--radius)', marginBottom:8}}>
                      <div>
                        <div style={{fontSize:13, fontWeight:500}}>{a.activite}</div>
                        <div style={{fontSize:12, color:'var(--text-2)', marginTop:2}}>
                          <span style={{
                            display:'inline-block', padding:'1px 7px', borderRadius:20, fontSize:11, fontWeight:500, marginRight:6,
                            background: a.niveau === 'competition' ? '#FAEEDA' : a.niveau === 'professionnel' ? '#FCEBEB' : '#EAF3DE',
                            color: a.niveau === 'competition' ? '#854F0B' : a.niveau === 'professionnel' ? '#A32D2D' : '#3B6D11',
                          }}>{NIVEAUX[a.niveau]}</span>
                          {a.frequence && <span>{a.frequence}</span>}
                        </div>
                      </div>
                      <button className="btn btn-sm btn-danger" onClick={() => deleteActivite(a.id)}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              {/* ── FAMILLE ── */}
              {activeTab === 'famille' && (
                <div>
                  {/* Règles facturation */}
                  {(() => {
                    const age = getAge(panel.date_naissance)
                    if (age === null) return null
                    if (age < 18) return (
                      <div style={{background:'#E6F1FB', borderLeft:'3px solid #378ADD', borderRadius:'0 var(--radius) var(--radius) 0', padding:'10px 12px', marginBottom:16, fontSize:12, color:'#0C447C'}}>
                        Patient mineur — la facture sera automatiquement adressée au parent marqué comme responsable de facturation.
                      </div>
                    )
                    if (age < 25) return (
                      <div style={{background:'#FAEEDA', borderLeft:'3px solid #BA7517', borderRadius:'0 var(--radius) var(--radius) 0', padding:'10px 12px', marginBottom:16, fontSize:12, color:'#854F0B'}}>
                        18-25 ans — au moment de la facture, vous pourrez choisir d'envoyer au patient ou à un parent lié.
                      </div>
                    )
                    return null
                  })()}

                  {/* Liens existants */}
                  {liens.length > 0 && (
                    <div style={{marginBottom:20}}>
                      {liens.map(l => (
                        <div key={l.id} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', border:'0.5px solid var(--border)', borderRadius:'var(--radius)', marginBottom:8}}>
                          <div>
                            <div style={{fontSize:13, fontWeight:500}}>{l.lie?.prenom} {l.lie?.nom}</div>
                            <div style={{fontSize:12, color:'var(--text-2)', marginTop:2}}>
                              {ROLES[l.role]}
                              {l.lie?.date_naissance && ` · ${getAge(l.lie.date_naissance)} ans`}
                            </div>
                          </div>
                          <div style={{display:'flex', alignItems:'center', gap:8}}>
                            <label style={{display:'flex', alignItems:'center', gap:5, fontSize:12, cursor:'pointer'}}>
                              <input type="checkbox" checked={l.facturation_principale} onChange={e => toggleFacturationPrincipale(l.id, e.target.checked)} />
                              Facturation
                            </label>
                            <button className="btn btn-sm btn-danger" onClick={() => deleteLien(l.id)}>✕</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Recherche patient à lier */}
                  <div style={{background:'var(--bg)', borderRadius:'var(--radius)', padding:'12px 14px'}}>
                    <div style={{fontSize:12, fontWeight:500, color:'var(--text-2)', marginBottom:10}}>Lier un membre de la famille</div>
                    <div className="form-row" style={{marginBottom:8}}>
                      <div className="form-group">
                        <label>Rôle</label>
                        <select value={newLienRole} onChange={e => setNewLienRole(e.target.value)}>
                          <option value="mere">Mère</option>
                          <option value="pere">Père</option>
                          <option value="tuteur">Tuteur/tutrice</option>
                          <option value="enfant">Enfant</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Rechercher le patient</label>
                        <input value={searchLien} onChange={e => searchPatientLien(e.target.value)} placeholder="Nom ou prénom…" />
                      </div>
                    </div>
                    {searchLienResults.length > 0 && (
                      <div style={{border:'0.5px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden'}}>
                        {searchLienResults.map(p => (
                          <div key={p.id} onClick={() => addLien(p)} style={{padding:'8px 12px', cursor:'pointer', fontSize:13, borderBottom:'0.5px solid var(--border)', background:'var(--surface)'}}
                            onMouseEnter={e => e.currentTarget.style.background='var(--bg)'}
                            onMouseLeave={e => e.currentTarget.style.background='var(--surface)'}>
                            {p.prenom} {p.nom} {p.date_naissance ? `· ${getAge(p.date_naissance)} ans` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                    {searchLien.length >= 2 && searchLienResults.length === 0 && (
                      <div style={{fontSize:12, color:'var(--text-3)'}}>Aucun patient trouvé</div>
                    )}
                  </div>
                </div>
              )}

              {/* ── SCHÉMA CORPOREL ── */}
              {activeTab === 'schema' && panel && panel !== 'new' && (
                <div style={{padding:'0 4px'}}>
                  <SchemaCorporel patientId={panel.id} readOnly={false} />
                </div>
              )}

              {/* ── CONSULTATIONS ── */}
              {activeTab === 'consultations' && (
                <div>
                  {consultations.length === 0 && factures.filter(f => f.source === 'terapiz').length === 0 && (
                    <div className="empty">Aucune consultation enregistrée</div>
                  )}
                  {/* Consultations enregistrées dans le logiciel */}
                  {consultations.map(c => {
                    let notesDisplay = c.notes_seance || ''
                    if (notesDisplay.startsWith('{') || notesDisplay.startsWith('"')) {
                      try {
                        const parsed = JSON.parse(notesDisplay)
                        notesDisplay = Object.values(parsed).filter(v => v && typeof v === 'string' && v.trim()).join('\n')
                      } catch {
                        notesDisplay = notesDisplay.replace(/\\n/g, '\n').replace(/[{}"]/g, '').replace(/select-\d+:\w+,?/g, '').trim()
                      }
                    }
                    return (
                    <div key={c.id} className="consult-item" style={{cursor:'pointer'}}
                      onClick={() => { /* [UNIFIE-PANEL v1] */ window.location.href = '/agenda?consult=' + c.id }}>
                      <div className="date">
                        {format(new Date(c.date_consultation.slice(0,10)), 'dd MMMM yyyy', {locale: fr}) + (c.date_consultation.slice(11,16) ? ' à ' + c.date_consultation.slice(11,16) : '')}
                        {' · '}{c.praticiens?.prenom} {c.praticiens?.nom}
                        {' · '}{c.duree_minutes} min · {c.tarif} €
                      </div>
                      {c.motif && <div style={{fontWeight:500, fontSize:13, marginBottom:4}}>{c.motif}</div>}
                      {c.motif_detail && <div className="note" style={{whiteSpace:'pre-line', marginBottom:4}}><strong style={{fontSize:11, color:'var(--text-3)'}}>Motif : </strong>{c.motif_detail}</div>}
                      {c.traitement && <div className="note" style={{whiteSpace:'pre-line', marginBottom:4}}><strong style={{fontSize:11, color:'var(--text-3)'}}>Traitement : </strong>{c.traitement}</div>}
                      {notesDisplay && <div className="note" style={{whiteSpace:'pre-line'}}>{notesDisplay}</div>}
                    </div>
                    )
                  })}
                  {/* Historique Terapiz (factures importées sans consultation associée) */}
                  {factures.filter(f => f.source === 'terapiz').length > 0 && (
                    <>
                      {consultations.length > 0 && (
                        <div style={{fontSize:11, color:'var(--text-3)', padding:'8px 0 4px', borderTop:'0.5px solid var(--border)', marginTop:8}}>
                          Historique Terapiz
                        </div>
                      )}
                      {factures.filter(f => f.source === 'terapiz').map(f => (
                        <div key={f.id} className="consult-item" style={{opacity:.75}}>
                          <div className="date">
                            {format(new Date(f.date_consultation || f.date_facture), 'dd MMMM yyyy', {locale: fr})}
                            {' · '}{f.montant} €
                            {' · '}<span style={{fontSize:11, color:'var(--text-3)'}}>Terapiz</span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* ── FACTURES ── */}
              {activeTab === 'factures' && (
                <div>
                  {factures.length === 0 && <div className="empty">Aucune facture (vos factures uniquement)</div>}
                  {factures.map(f => (
                    <div key={f.id} className="consult-item">
                      <div className="date">{f.numero} · {format(new Date(f.date_facture), 'dd/MM/yyyy')}</div>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:4}}>
                        <span style={{fontWeight:500}}>{f.montant} €</span>
                        <span className={`badge ${f.statut === 'payee' ? 'badge-green' : f.statut === 'annulee' ? 'badge-red' : 'badge-amber'}`}>
                          {f.statut === 'payee' ? 'Payée' : f.statut === 'annulee' ? 'Annulée' : 'En attente'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="panel-footer">
              {panel !== 'new' && <button className="btn btn-danger btn-sm" onClick={deletePatient}>Supprimer</button>}
              <div style={{flex:1}} />
              <button className="btn btn-sm" onClick={() => setPanel(null)}>Fermer</button>
              {(panel === 'new' || activeTab === 'info') && (
                <button className="btn btn-primary btn-sm" onClick={savePatient} disabled={saving || !form.prenom || !form.nom}>
                  {saving ? 'Enregistrement…' : panel === 'new' ? 'Créer le patient' : 'Enregistrer'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}
